import React from 'react';

import { Box, Button, Heading, Text, ThemeConstellation } from '@zillow/constellation';
import { useViewerState } from '@zillow/shopper-platform-config';
import { useYieldCallback } from '@zillow/yield-callback';

import DESKTOP_JPEG_VISUAL_REFRESH from '../../../public/images/GatedCarousel-lg.jpg';
import DESKTOP_WEBP_VISUAL_REFRESH from '../../../public/images/GatedCarousel-lg.webp';
import MOBILE_JPEG_VISUAL_REFRESH from '../../../public/images/GatedCarousel-sm.jpg';
import MOBILE_WEBP_VISUAL_REFRESH from '../../../public/images/GatedCarousel-sm.webp';
import { handleSignInClick } from './gatedCollectionUtils';

const GATED_SIGN_IN_BUTTON = 'gated-sign-in-btn';

const useImagesConfig = () => ({
    DESKTOP_JPEG: DESKTOP_JPEG_VISUAL_REFRESH,
    DESKTOP_WEBP: DESKTOP_WEBP_VISUAL_REFRESH,
    MOBILE_JPEG: MOBILE_JPEG_VISUAL_REFRESH,
    MOBILE_WEBP: MOBILE_WEBP_VISUAL_REFRESH,
    DESKTOP_IMG_SIZES: {
        width: 464,
        height: 360,
    },
    MOBILE_IMG_SIZES: {
        width: 320,
        height: 248,
    },
    // Breakpoint used to decide whether to use desktop or mobile view
    DESKTOP_VIEW_BREAKPOINT: ThemeConstellation.constellation.breakpoints.md,
});

const GatedHomeRecs: React.FC = () => {
    const {
        DESKTOP_JPEG,
        DESKTOP_WEBP,
        MOBILE_JPEG,
        MOBILE_WEBP,
        DESKTOP_IMG_SIZES,
        MOBILE_IMG_SIZES,
        DESKTOP_VIEW_BREAKPOINT,
    } = useImagesConfig();

    const { isLoggedIn } = useViewerState();

    const yieldedHandleSignInClick = useYieldCallback(handleSignInClick);

    return (
        <Box
            sx={{
                'position': 'relative',
                'display': 'flex',
                'flexDirection': 'column',
                'alignItems': 'center',
                'overflowX': 'hidden',
                'justifyContent': 'space-evenly',
                '@media $lg_gte': {
                    flexDirection: 'row',
                },
            }}
        >
            <Box
                sx={{
                    'textAlign': 'center',
                    'marginBottom': '$spacing.lg',
                    'flex': 1,
                    '@media $lg': {
                        textAlign: 'left',
                        marginBottom: '$spacing.none',
                    },
                    '@media $xl': {
                        flex: 'none',
                    },
                }}
            >
                <Heading level={5} marginBottom="xs">
                    {isLoggedIn ? 'Recommendations underway' : 'Get home recommendations'}
                </Heading>
                <Text as="p" fontColor="textMedium">
                    {isLoggedIn
                        ? "Search and save a few homes you like and we'll find recommendations for you."
                        : 'Sign in for a more personalized experience.'}
                </Text>
                {!isLoggedIn && (
                    <Button
                        marginTop="sm"
                        id={GATED_SIGN_IN_BUTTON}
                        onClick={yieldedHandleSignInClick}
                    >
                        Sign in
                    </Button>
                )}
            </Box>

            <Box
                sx={{
                    overflow: 'hidden',
                    width: MOBILE_IMG_SIZES.width,
                    height: MOBILE_IMG_SIZES.height,

                    [`@media screen and (min-width: ${DESKTOP_VIEW_BREAKPOINT}px)`]: {
                        width: DESKTOP_IMG_SIZES.width,
                        height: DESKTOP_IMG_SIZES.height,
                    },
                }}
            >
                <picture>
                    <source
                        srcSet={DESKTOP_WEBP}
                        media={`(min-width: ${DESKTOP_VIEW_BREAKPOINT}px)`}
                        type="image/webp"
                        width={DESKTOP_IMG_SIZES.width}
                        height={DESKTOP_IMG_SIZES.height}
                    />
                    <source
                        srcSet={DESKTOP_JPEG}
                        media={`(min-width: ${DESKTOP_VIEW_BREAKPOINT}px)`}
                        type="image/jpeg"
                        width={DESKTOP_IMG_SIZES.width}
                        height={DESKTOP_IMG_SIZES.height}
                    />
                    <source
                        srcSet={MOBILE_WEBP}
                        media={`(max-width: ${DESKTOP_VIEW_BREAKPOINT}px)`}
                        type="image/webp"
                        width={MOBILE_IMG_SIZES.width}
                        height={MOBILE_IMG_SIZES.height}
                    />
                    <img
                        src={MOBILE_JPEG}
                        // This is just a display image, so hide it from screen
                        // readers by setting the alt text to an empty string.
                        alt=""
                        width={MOBILE_IMG_SIZES.width}
                        height={MOBILE_IMG_SIZES.height}
                    />
                </picture>
            </Box>
        </Box>
    );
};

export default GatedHomeRecs;
